import * as React from "react";
import styled from "@emotion/styled";
import { Global } from "@emotion/react";
import { useLocation } from "react-router-dom";
import {
  AllCaps,
  Avatar,
  Button,
  Container,
  H1,
  Hr,
  IconBell,
  H3,
  IconClose,
  IconMenu,
  Link,
  Text,
  ScreenReaderText,
  FlexRow,
  FadeIn,
  IconChevron,
  IconExternalLink,
  IconUser,
  IconDiamond,
  Stack,
  IconHugLogo,
  List,
} from "atoms";
import { useAuth, useWalletAuth } from "features/auth";
import {
  AnnouncementList,
  NotificationContent,
  useNotifications,
  useMarkNotificationsAsRead,
} from "features/notifications";
import { HUGS, useProfilePoints } from "features/profile";
import { GlobalSearch } from "features/search";
import { useArtist, useNewArtistApplication } from "features/reviewable";
import { usePendingReviews } from "features/reviews";
import { constants } from "config";
import { trackEvent } from "contexts/mixpanel";

const trackSignIn = () =>
  trackEvent("Button Click", {
    ButtonName: "Sign In",
    ButtonContext: "Site Header",
    Location: window.location.href,
  });

interface HeaderProps {
  minimal?: boolean;
}

function SiteHeader({ minimal = false }: HeaderProps) {
  const { user } = useAuth();

  // Notifications
  const { data } = useNotifications();
  const { mutate, isLoading: markAsReadIsLoading } =
    useMarkNotificationsAsRead();
  const unreadNotifications = data ? data.filter((d) => !d.readAt) : [];
  const unreadNotificationsList = unreadNotifications.slice(0, 4);
  const unreadCount = unreadNotifications.length;

  const markAllAsRead = () =>
    mutate(unreadNotifications.map((d) => d.notificationId));

  const location = useLocation();

  // Site header visibility
  const [isHeaderShowing, setIsHeaderShowing] = React.useState(true);
  const lastKnownScrollPosition = React.useRef<number>(window.scrollY);
  const scrolled = React.useRef<boolean>(false);

  // handles site header dropdown states
  const [activeDropdown, setActiveDropdown] = React.useState("");
  const resetDropdowns = () => setActiveDropdown("");
  const createActiveHandler = (dropdown: string) => () =>
    activeDropdown === dropdown
      ? resetDropdowns()
      : setActiveDropdown(dropdown);

  // On navigation change, make sure dropdownsare hidden
  React.useEffect(() => {
    setActiveDropdown("");
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [location]);

  /**
   * When focus leaves the parent element of our dropdown menus, reset to close them.
   * NOTE: we're running this event on the capture phase to prevent child elements firing this same event handler.
   */
  const onBlurCapture: React.FocusEventHandler = (event) => {
    // Event will travel from root element to this element, but we want to stop it from continuing to this element's children
    event.stopPropagation();
    // Current target is the element we attached the handler to, and the related target is the newly focused element. If the new
    // element is not a descendent of the current target, then we are outside of the nav element.
    if (!event.currentTarget.contains(event.relatedTarget)) {
      resetDropdowns();
    }
  };

  // Hides all dropdowns when one is open and screen is resized above 831px
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 831) {
        setActiveDropdown("");
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  React.useLayoutEffect(() => {
    const checkPosition = () => {
      // shows header at the top or bottom of page
      const atTop = window.scrollY < 100;
      const atBottom =
        Math.round(document.documentElement.getBoundingClientRect().height) <
        window.scrollY + window.innerHeight + 300;
      const scrollsUp = lastKnownScrollPosition.current - window.scrollY >= 0;

      setIsHeaderShowing(atTop || atBottom || scrollsUp);

      lastKnownScrollPosition.current = window.scrollY;
      scrolled.current = false;
    };

    const scrollHandler = () => {
      if (!scrolled.current) {
        scrolled.current = true;
        window.requestAnimationFrame(checkPosition);
      }
    };

    const timeout = window.setTimeout(() => {
      window.addEventListener("scroll", scrollHandler);
    }, 200);

    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener("scroll", scrollHandler);
    };
  }, []);

  const globalCSS = {
    "#root": {
      ...(activeDropdown !== "" &&
        window.innerWidth < 831 && {
          height: "100vh",
          width: "100vw",
          overflow: "hidden",
        }),
    },
  };

  const isHomePage = location.pathname === "/";
  const showAnnouncements = !minimal && isHomePage;

  return (
    <Header isHeaderShowing={isHeaderShowing}>
      <Global styles={globalCSS} />

      {showAnnouncements && <AnnouncementList />}

      <SContainer width="lg">
        <Logo>
          <LogoLink to="/" aria-label="Home">
            <IconHugLogo aria-label="Hug Logo" />
          </LogoLink>
        </Logo>

        {
          !minimal && (
            <>
              <GlobalSearchWrapper>
                <GlobalSearch />
              </GlobalSearchWrapper>

              <SiteNavWrapper onBlur={onBlurCapture}>
                <SiteNav
                  aria-label="Main navigation"
                  className="mainNavigation"
                >
                  <Dropdown
                    isOpen={activeDropdown === "nav"}
                    onBlur={onBlurCapture}
                  >
                    <NavDropdownToggle
                      aria-haspopup="true"
                      type="button"
                      onClick={createActiveHandler("nav")}
                      aria-label={`${activeDropdown ? "Close" : "Open"} Menu`}
                      variant="secondary"
                    >
                      {activeDropdown === "nav" ? (
                        <IconClose aria-hidden="true" />
                      ) : (
                        <IconMenu aria-hidden="true" />
                      )}
                    </NavDropdownToggle>

                    <NavDropdownMenu
                      className="DropdownMenu"
                      aria-label="submenu"
                    >
                      <SubMenuDropdown isOpen={activeDropdown === "explore"}>
                        <SubMenuDropdownToggle
                          onClick={createActiveHandler("explore")}
                        >
                          Explore <IconChevron className="DropdownArrow" />
                        </SubMenuDropdownToggle>
                        <SubMenuDropdownMenu>
                          <SubMenuHeading>Explore</SubMenuHeading>
                          <SubMenuLink to="/search?type=artist&sort=reviewCount+desc">
                            Artist Profiles
                          </SubMenuLink>
                          <SubMenuLink to="/artist-updates">
                            Artist Updates
                          </SubMenuLink>
                          <SubMenuLink to="/collectives">
                            Artist Collectives
                          </SubMenuLink>
                          <SubMenuLink to="/open-calls">Open Calls</SubMenuLink>
                        </SubMenuDropdownMenu>
                      </SubMenuDropdown>

                      <SubMenuDropdown isOpen={activeDropdown === "getStarted"}>
                        <SubMenuDropdownToggle
                          onClick={createActiveHandler("getStarted")}
                        >
                          Get Started <IconChevron className="DropdownArrow" />
                        </SubMenuDropdownToggle>
                        <SubMenuDropdownMenu>
                          <SubMenuHeading>Get Started</SubMenuHeading>
                          <SubMenuLink to="/artist-profile-benefits">
                            Apply for Artist Profile
                          </SubMenuLink>
                          <SubMenuLink
                            href={constants.openseaURL}
                            target="_blank"
                          >
                            <span>
                              Buy a <AllCaps>Hug</AllCaps> Pass
                            </span>{" "}
                            <IconExternalLink />
                          </SubMenuLink>
                          <SubMenuLink to="/commissions">
                            Commission Artwork
                          </SubMenuLink>
                        </SubMenuDropdownMenu>
                      </SubMenuDropdown>

                      <SubMenuDropdown isOpen={activeDropdown === "resources"}>
                        <SubMenuDropdownToggle
                          onClick={createActiveHandler("resources")}
                        >
                          Resources <IconChevron className="DropdownArrow" />
                        </SubMenuDropdownToggle>
                        <SubMenuDropdownMenu>
                          <SubMenuHeading>Resources</SubMenuHeading>
                          <SubMenuLink to={constants.studiosUrl}>
                            <span>
                              <AllCaps>Hug</AllCaps> Studios
                            </span>
                          </SubMenuLink>
                          <SubMenuLink to="/resources">
                            Free Guides &amp; Tools
                          </SubMenuLink>
                          <SubMenuLink
                            href={constants.creatorRoyaltiesURL}
                            target="_blank"
                          >
                            Weekly Creator Newsletter <IconExternalLink />
                          </SubMenuLink>
                          <SubMenuLink to="/faq">FAQs</SubMenuLink>
                          <SubMenuLink to="/banner-maker">
                            Banner Maker
                          </SubMenuLink>
                        </SubMenuDropdownMenu>
                      </SubMenuDropdown>
                    </NavDropdownMenu>
                  </Dropdown>
                </SiteNav>
              </SiteNavWrapper>

              <SiteAccount onBlur={onBlurCapture}>
                <FlexRow>
                  {!!user && (
                    <Dropdown isOpen={activeDropdown === "notifications"}>
                      <DropdownToggle
                        onClick={createActiveHandler("notifications")}
                        arrow={null}
                        aria-haspopup="true"
                        aria-label={`${unreadCount} new notifications`}
                      >
                        <NotificationIconWrapper>
                          <IconBell aria-hidden="true" accent="accent3" />
                          {unreadCount > 0 && (
                            <NotificationCount>{unreadCount}</NotificationCount>
                          )}
                        </NotificationIconWrapper>
                      </DropdownToggle>

                      <DropdownMenu aria-label="submenu">
                        <NotificationDropdownMenuHeader>
                          <H3 size="xs" textTransform="uppercase">
                            {unreadCount > 0 &&
                              `${unreadCount} unread notification${
                                unreadCount !== 1 ? "s" : ""
                              }`}
                            {unreadCount <= 0 && "Recent notifications"}
                          </H3>

                          {unreadCount > 0 && (
                            <MarkAsReadButton
                              type="button"
                              variant="blank"
                              size="xxs"
                              onClick={markAllAsRead}
                              disabled={markAsReadIsLoading}
                            >
                              Mark all as read
                            </MarkAsReadButton>
                          )}
                        </NotificationDropdownMenuHeader>

                        {unreadNotifications.length > 0 ? (
                          <NotificationList>
                            {unreadNotificationsList.map((n) => (
                              <li key={n.notificationId}>
                                <NotificationContent
                                  isDismissible
                                  notification={n}
                                />
                              </li>
                            ))}
                          </NotificationList>
                        ) : (
                          <Text size="xxs">You have no new notifications.</Text>
                        )}

                        <Link
                          to="/profile/notifications"
                          size="xs"
                          variant="secondary"
                        >
                          View all notifications
                        </Link>
                      </DropdownMenu>
                    </Dropdown>
                  )}

                  {user && (
                    <AccountDropdown
                      unreadCount={unreadCount}
                      isActive={activeDropdown === "account"}
                      toggleAccountShowing={createActiveHandler("account")}
                    />
                  )}

                  {!user && (
                    <SignInButton
                      to="/sign-in"
                      size="sm"
                      variant="primary"
                      onClick={trackSignIn}
                    >
                      Sign In <IconUser />
                    </SignInButton>
                  )}
                </FlexRow>
              </SiteAccount>
            </>
          )
          // Ends minimal style
        }
      </SContainer>
    </Header>
  );
}

type AccountDropdownProps = {
  unreadCount: number;
  isActive: boolean;
  toggleAccountShowing: VoidFunction;
};

function AccountDropdown({
  isActive,
  toggleAccountShowing,
  unreadCount,
}: AccountDropdownProps) {
  const {
    user,
    roles: { isSrCurator, isQA, showAdminFeatures, isHugger },
    signOut,
  } = useAuth();
  const { disconnect } = useWalletAuth();

  const { data: profilePoints } = useProfilePoints(user?.id ?? "");

  // Notification badges for Moderation and Application Voting
  // Handle signout and wallet disconnect
  const handleSignOut = () => {
    disconnect();
    signOut();
  };

  // Determine user's avatar src or fallback to HUG token
  let avatarSrc;
  if (user?.avatar) {
    avatarSrc = user.avatar.url150 ?? user.avatar.url300 ?? user.avatar.url;
  } else {
    avatarSrc = `https://thehug.xyz/assets/tokens/${
      HUGS[user?.hugImageIds?.[0] ?? 0].image
    }`;
  }

  return (
    <Dropdown isOpen={isActive}>
      <AvatarDropdownToggle
        onClick={toggleAccountShowing}
        arrow={null}
        aria-haspopup="true"
        aria-label="My account"
      >
        <Avatar
          alt="avatar"
          src={avatarSrc}
          aria-hidden="true"
          size={buttonSize}
        />
      </AvatarDropdownToggle>

      <DropdownMenu className="DropdownMenu" aria-label="submenu">
        <Stack gap="xs">
          <SubMenuLink to="/profile">
            My Account
            {profilePoints && (
              <SScore>
                <IconDiamond accent="accent1" />{" "}
                <span>{profilePoints?.pointsBalance.toLocaleString()}</span>
                <ScreenReaderText as="span">HUG Points</ScreenReaderText>
              </SScore>
            )}
          </SubMenuLink>

          <ArtistProfileLinks
            artistProfiles={user?.claimedArtistPageIds ?? []}
          />

          <MobileSubMenuLink to="/profile/notifications/">
            Notifications
            {unreadCount > 0 && (
              <NotificationCount>{unreadCount}</NotificationCount>
            )}
          </MobileSubMenuLink>
        </Stack>

        <SHr />

        <Stack gap="xs">
          <SubMenuLink to="/artist-invites">Invite Artists</SubMenuLink>
          {isSrCurator && <ModerationLink />}
          {isHugger && <ArtistVoteLink />}
        </Stack>

        {(showAdminFeatures || isQA) && <SHr />}

        <Stack gap="xs">
          {showAdminFeatures && (
            <SubMenuLink to="/admin/users">Admin</SubMenuLink>
          )}

          {(showAdminFeatures || isQA) && (
            <>
              <SubMenuLink to="/qa/tickets">Quality Assurance</SubMenuLink>
              <SubMenuLink to="/search?status=DRAFT">
                Draft Creators
              </SubMenuLink>
            </>
          )}
        </Stack>

        <SHr />

        <Stack gap="xs">
          <SubMenuLink to="/redeem-hug-points">Redeem Hug Points</SubMenuLink>
          <SubMenuLink to="/profile/mobileSignIn">Mobile Sign In</SubMenuLink>
          <SubMenuLink to="/" onClick={handleSignOut}>
            Sign Out
          </SubMenuLink>
        </Stack>
      </DropdownMenu>
    </Dropdown>
  );
}

function ArtistName({ artistId }: { artistId: string }) {
  const { data: artist } = useArtist(artistId);
  const name = artist?.name;
  return <>{name}</>;
}
function ArtistProfileLinks({ artistProfiles }: { artistProfiles: string[] }) {
  const artistProfileCount = artistProfiles.length ?? 0;

  if (artistProfileCount === 0) return null;

  if (artistProfileCount === 1)
    return (
      <SubMenuLink to={`/artists/${artistProfiles[0]}`}>
        My Artist Profile
      </SubMenuLink>
    );

  return (
    <>
      <SHr />
      <H3 size="xxs" style={{ opacity: 0.85, marginTop: "1rem" }}>
        My Artist Profiles
      </H3>
      <List type="blank">
        <Stack gap="xxs">
          {artistProfiles.map((artistId) => (
            <Text size="xxs" bold key={artistId}>
              <SLink to={`/artists/${artistId}`}>
                <ArtistName artistId={artistId} />
              </SLink>
            </Text>
          ))}
        </Stack>
      </List>
    </>
  );
}

function ModerationLink() {
  const { data: pendingReviews } = usePendingReviews();
  const moderationCount = pendingReviews?.notModeratedAvailable ?? 0;

  return (
    <SubMenuLink to="/moderation">
      Moderate Reviews
      {moderationCount > 0 && (
        <NotificationCount>{moderationCount}</NotificationCount>
      )}
    </SubMenuLink>
  );
}

function ArtistVoteLink() {
  const { data: newApplication } = useNewArtistApplication();
  const applicationCount = newApplication?.notReactedAvailable ?? 0;

  return (
    <SubMenuLink to="/artist-voting">
      Vote on Artists
      {applicationCount > 0 && (
        <NotificationCount>{applicationCount}</NotificationCount>
      )}
    </SubMenuLink>
  );
}

const searchInputHeight = 50;
const buttonSize = 36;

const SContainer = styled(Container)(({ theme }) => ({
  alignItems: "center",
  display: "grid",
  gridTemplateAreas: `
    "logo   account nav"
    "search search  search"
  `,
  gridTemplateColumns: "1fr auto auto",
  gap: "0 1rem",
  // height: headerHeight,
  // justifyContent: "space-between",
  position: "relative",
  zIndex: 3,
  [theme.breakpoints.tablet]: {
    gap: `0 ${theme.spacing.gap}`,
    gridTemplateAreas: `
      "logo   nav     account"
      "search search  search"
    `,
    gridTemplateColumns: "auto 1fr auto",
  },
  [theme.breakpoints.desktop]: {
    gap: `0 ${theme.spacing.gap}`,
    gridTemplateAreas: `"logo nav search account"`,
    gridTemplateColumns: "auto 3fr 2fr auto",
  },
}));

const Header = styled.header<{ isHeaderShowing: boolean }>(
  ({ isHeaderShowing, theme }) => ({
    background: theme.colors.bg,
    position: "sticky",
    top: 0,
    "@media (prefers-reduced-motion: no-preference)": {
      transition: "all 0.5s ease",
    },
    width: "100%",
    zIndex: 500,
    opacity: 1,
    transform: "translateY(0)",
    paddingTop: "env(safe-area-inset-top)",
    ...(!isHeaderShowing && {
      opacity: 0,
      transform: "translateY(-100%)",
    }),
    "&::before": {
      boxShadow: theme.boxShadow.light,
      bottom: 0,
      content: "''",
      height: 20,
      left: 0,
      position: "absolute",
      right: 0,
      "@media (prefers-reduced-motion: no-preference)": {
        transition: "all 0.5s ease",
      },
      zIndex: 1,
      ...(!isHeaderShowing && {
        boxShadow: "none",
      }),
    },
    "&::after": {
      content: "''",
      position: "absolute",
      backgroundColor: theme.colors.bg,
      bottom: 0,
      left: 0,
      right: 0,
      top: 0,
      zIndex: 2,
    },
  })
);

const Logo = styled(H1)({
  gridArea: "logo",
  margin: 0,
  width: "calc(55px + 2vw)",
  paddingTop: 10,
  paddingBottom: 10,
});

const LogoLink = styled(Link)({
  height: "auto",
  width: "calc(55px + 2vw)",
  display: "block",
  lineHeight: 0,
  margin: 0,
  "@media (prefers-reduced-motion: no-preference)": {
    transition: "transform 0.15s ease",
  },
  "&:hover": {
    // transform: "scale(1.1) translate(2px, 2px)",
  },
  "&:active": {
    // transform: "scale(1, 1.2) translate(2px, 2px)",
  },
  svg: {
    display: "block",
    height: "auto",
    width: "auto",
  },
});
const SiteNavWrapper = styled.div({
  gridArea: "nav",
  paddingTop: 10,
  paddingBottom: 10,
});

const GlobalSearchWrapper = styled.div({
  gridArea: "search",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
});

const SiteAccount = styled.div({
  gridArea: "account",
  paddingTop: 10,
  paddingBottom: 10,
});

const SiteNav = styled.nav(({ theme }) => ({
  background: theme.colors.bg,
  display: "flex",
  [theme.breakpoints.tablet]: {
    alignItems: "center",
    background: "none",
    display: "flex",
    flex: "0 0 auto",
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: "1em",
    height: "auto",
    overflow: "visible",
    paddingRight: 0,
    position: "static",
  },
}));

// eslint-disable-next-line
const HeaderLink: any = styled(Link)(({ theme }) => ({
  ...theme.fonts.display,
  textTransform: "uppercase",
  textDecoration: "none",
  display: "flex",
  gap: 5,
  alignItems: "center",
  margin: "0 0 1rem",
  maxWidth: "max-content",
  padding: "0.5rem",
  border: 0,
  borderWidth: "2px 0 2px 0",
  borderStyle: "solid",
  borderColor: theme.colors.transparent,
  cursor: "pointer",
  transition: ".2s ease border-color",
  "&:hover, &:focus-visible": {
    borderBottomColor: theme.colors.accent1,
  },
  svg: {
    width: ".8em",
    height: "auto",
  },
  [theme.breakpoints.maxTablet]: {
    fontSize: 20,
  },
  [theme.breakpoints.tablet]: {
    margin: 0,
    padding: 0,
    "&:hover, &:focus-visible": {
      textDecoration: "none",
    },
  },
}));

const SignInButton = styled(Link)(({ theme }) => ({
  justifyContent: "center",
  marginLeft: 10,
  order: 99,
  whiteSpace: "nowrap",
  [theme.breakpoints.tablet]: {
    fontSize: theme.fontSizes.sm,
    marginTop: "0",
  },
}));

const Dropdown = styled.div<{ isOpen: boolean; disabled?: boolean }>(
  ({ isOpen, disabled, theme }) => ({
    alignItems: "center",
    alignSelf: "stretch",
    display: "inline-flex",
    textAlign: "left",
    ...(!disabled &&
      isOpen && {
        "&>.DropdownMenu": {
          display: "block",
          minWidth: 200,
          opacity: 1,
          visibility: "visible",
          zIndex: 101,
          [theme.breakpoints.tablet]: {
            width: "max-content",
            maxWidth: 400,
          },
        },

        "& .DropdownArrow": {
          transform: "rotate(0deg)",
        },
      }),
    [theme.breakpoints.tablet]: {
      position: "relative",
    },

    "button .DropdownArrow": {
      transform: isOpen ? "rotate(0deg)" : "rotate(180deg)",
    },
  })
);

const DropdownToggle = styled(Button)(({ theme }) => ({
  ...theme.fonts.display,
  appearance: "none",
  backgroundColor: theme.colors.black,
  border: 0,
  display: "flex",
  fontWeight: "400",
  fontSize: theme.fontSizes.lg,
  minWidth: buttonSize,
  padding: 0,

  ".DropdownArrow": {
    display: "none",
    fontSize: ".75em",
    pointerEvents: "none",
    [theme.breakpoints.tablet]: {
      display: "inline-block",
    },
  },

  img: {
    height: buttonSize,
    width: buttonSize,
  },
  "&:hover, &:focus": {
    color: theme.colors.fg,
    transform: "none",
  },
  "&:disabled": {
    background: "unset",
    color: theme.colors.fg,
  },
  [theme.breakpoints.tablet]: {
    alignItems: "center",
    padding: 0,
    zIndex: 1,
  },
}));

const DropdownMenu = styled(FadeIn)(({ theme }) => ({
  [theme.breakpoints.maxTablet]: {
    "body &&": {
      width: "100%",
      maxWidth: "100%",
    },
    ".DropdownMenu .DropdownMenu": {
      width: "100%",
    },
    ".DropdownMenu &": {
      width: "100%",
    },
  },
  position: "absolute",
  top: searchInputHeight + 5,
  right: 0,
  width: "100%",
  display: "none",
  backgroundColor: theme.colors.bg,
  borderRadius: theme.borderRadius.md,
  boxShadow: theme.boxShadow.light,
  paddingTop: theme.spacing.md,
  padding: "1.5rem",
  ".mainNavigation &>*": {
    marginBottom: 0,
    marginTop: 0,
  },
  [theme.breakpoints.maxTablet]: {
    "&&>*+*": {
      marginTop: theme.spacing.md,
    },
  },
  ".DropdownMenu &": {
    // width: "max-content",
    "&>*+*": {
      marginTop: theme.spacing.sm,
    },
  },
  [theme.breakpoints.tablet]: {
    background: theme.colors.bgLight,
    borderRadius: theme.borderRadius.md,
    boxShadow: theme.boxShadow.dark,
    opacity: 0,
    minWidth: 300,
    width: "max-content",
    padding: `${theme.spacing.md} calc(${theme.spacing.sm} + 6px)`,
    right: -5,
    top: "calc(100% + 10px)",
    visibility: "hidden",
    "@media (prefers-reduced-motion: no-preference)": {
      transition: "opacity ease-in 0.2s",
    },
    "&::before, &::after": {
      border: "solid rgb(0 0 0 / 0)",
      borderBottomColor: theme.colors.fg20,
      bottom: "100%",
      borderWidth: 10,
      marginLeft: "-10px",
      content: "''",
      height: 0,
      width: 0,
      position: "absolute",
      pointerEvents: "none",
      left: "calc(100% - 23px)",
      transform: "scaleY(.9)",
      transformOrigin: "bottom",
    },
    "&::after": {
      borderBottomColor: theme.colors.bg,
      borderWidth: 9,
      marginLeft: "-9px",
    },
  },
}));
DropdownMenu.defaultProps = { className: "DropdownMenu" };

const NavDropdownToggle = styled(DropdownToggle)(({ theme }) => ({
  appearance: "none",
  backgroundColor: theme.colors.transparent,
  display: "inline-flex",
  justifyContent: "center",
  padding: 0,
  gap: 0,
  height: buttonSize,
  width: buttonSize,
  "&:hover, &:focus-visible": {
    backgroundColor: theme.colors.transparent,
  },
  svg: {
    fontSize: 26,
    margin: 0,
  },
  [theme.breakpoints.tablet]: {
    display: "none",
  },
}));

const NavDropdownMenu = styled(DropdownMenu)(({ theme }) => ({
  padding: "2.5rem 1.5rem",
  [theme.breakpoints.tablet]: {
    alignItems: "center",
    boxShadow: "none",
    borderRadius: 0,
    display: "flex",
    justifyContent: "flex-start",
    visibility: "visible",
    margin: 0,
    position: "static",
    padding: 0,
    opacity: "1",
    gap: "1rem",
    "&>*": {
      margin: 0,
      whiteSpace: "nowrap",
    },
    "body &>*+*": {
      margin: "unset",
    },
    "&::before, &::after": {
      content: "none",
    },
  },
  [theme.breakpoints.desktop]: {
    alignItems: "center",
    justifyContent: "flex-start",
  },
}));

const SubMenuLink = styled(HeaderLink)(({ theme }) => ({
  gap: 10,
  lineHeight: 1.3,
  margin: 0,
  padding: 0,
  "&:hover, &:focus": {
    color: theme.colors.fg,
  },
  "&::after": {
    content: "none",
  },
  [theme.breakpoints.tablet]: {
    margin: `${theme.spacing.sm} 0`,
    fontSize: theme.fontSizes.xs,
  },
}));

const MobileSubMenuLink = styled(SubMenuLink)({
  "@media (min-width: 831px)": {
    display: "none",
  },
});

const Score = styled(Text)(({ theme }) => ({
  ...theme.fonts.body,
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontWeight: "bold",
  width: "min-content",
  color: theme.colors.fg,
  lineHeight: "1",
  margin: "0 0 0 8px",
  fontSize: 14,
  textTransform: "none",
  whiteSpace: "nowrap",
  span: {
    position: "relative",
    top: 1,
  },
  svg: {
    display: "inline-block",
    fontSize: "1rem",
  },
}));

const SScore = styled(Score)({
  display: "inline-flex",
  alignItems: "center",
  margin: "0 0 0 10px",
  width: "auto",
  fontSize: 14,
});

const SHr = styled(Hr)(({ theme }) => ({
  height: 1,
  margin: "1.25em 0",
  opacity: ".25",
  [theme.breakpoints.tablet]: {
    margin: ".8em 0",
  },
}));

const NotificationCount = styled.span(({ theme }) => ({
  ...theme.fonts.display,
  alignItems: "center",
  backgroundColor: theme.colors.accent3,
  borderRadius: theme.borderRadius.round,
  display: "flex",
  letterSpacing: "-.02em",
  textAlign: "center",
  fontSize: 13,
  fontWeight: "normal",
  height: 21,
  justifyContent: "center",
  minWidth: 21,
  padding: "0 2px 1px 3px",
  maxWidth: "max-content",
  color: theme.colors.black,
}));
NotificationCount.defaultProps = { className: "NotificationCount" };

const NotificationIconWrapper = styled.div(({ theme }) => ({
  alignItems: "center",
  alignSelf: "center",
  backgroundColor: theme.colors.black,
  border: `2px solid ${theme.colors.black}`,
  borderRadius: theme.borderRadius.round,
  display: "flex",
  fontSize: 18,
  justifyContent: "center",
  height: buttonSize,
  padding: 0,
  width: buttonSize,
  "&:hover, *:focus>&": {
    backgroundColor: theme.colors.white,
    color: theme.colors.black,
  },
  ".NotificationCount": {
    position: "absolute",
    top: 0,
    right: 0,
    transform: "translate(45%, -25%)",
  },
}));

const NotificationDropdownMenuHeader = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  margin: "8px 0",
  gap: 10,
  "body &>*": {
    margin: 0,
  },
});

const MarkAsReadButton = styled(Button)(({ theme }) => ({
  ...theme.fonts.body,
  fontWeight: "normal",
  textTransform: "none",
}));

const NotificationList = styled.ul(({ theme }) => ({
  listStyleType: "none",
  padding: 0,
  margin: "1em 0",
  fontSize: theme.fontSizes.xxxs,
  lineHeight: 1.3,
  li: {
    borderBottom: `1px solid ${theme.colors.fg20}`,
    borderTop: `1px solid ${theme.colors.fg20}`,
    padding: "1em 0",
  },
  "li+li": {
    borderTop: "0",
  },
  "&>li::before": {
    content: `"\\200B"`,
    position: "absolute",
  },
  "li span": {
    display: "none",
  },
}));

const SubMenuDropdown = styled(Dropdown)<{ isOpen: boolean }>(({ theme }) => ({
  [theme.breakpoints.maxTablet]: {
    display: "block",
    "*+&": {
      margin: "2rem 0 0 0",
    },
  },
}));

const SubMenuDropdownToggle = styled(DropdownToggle)(({ theme }) => ({
  padding: 0,
  background: theme.colors.transparent,
  borderRadius: 0,
  whiteSpace: "nowrap",
  fontSize: theme.fontSizes.md,
  pointerEvents: "none",
  border: 0,
  borderWidth: "2px 0 2px 0",
  borderStyle: "solid",
  borderColor: theme.colors.transparent,
  cursor: "pointer",
  transition: ".2s ease border-color",
  "&:hover, &:focus-visible": {
    borderColor: theme.colors.transparent,
    borderBottomColor: theme.colors.accent1,
    backgroundColor: theme.colors.transparent,
  },
  [theme.breakpoints.maxTablet]: {
    display: "none",
  },
  [theme.breakpoints.tablet]: {
    "body &": {
      color: theme.colors.black,
    },
    fontSize: theme.fontSizes.sm,
    pointerEvents: "auto",
  },
}));

const SubMenuHeading = styled.p(({ theme }) => ({
  ...theme.fonts.display,
  color: theme.colors.gray60,
  textTransform: "uppercase",
  fontSize: theme.fontSizes.xs,
  [theme.breakpoints.tablet]: {
    display: "none",
  },
}));

const SubMenuDropdownMenu = styled(DropdownMenu)(({ theme }) => ({
  position: "absolute",
  [theme.breakpoints.maxTablet]: {
    borderRadius: 0,
    boxShadow: "none",
    display: "block",
    position: "static",
    padding: 0,
    paddingTop: theme.spacing.lg,
  },
  [theme.breakpoints.tablet]: {
    borderTop: 0,
    right: "auto",
    left: -5,
    "&::before, &::after": {
      marginLeft: -10,
      left: "auto",
      right: "calc(100% - 23px)",
    },
    "&::after": {
      borderWidth: 9,
      marginLeft: -9,
    },
  },
}));

const AvatarDropdownToggle = styled(DropdownToggle)({
  filter: "grayscale(1) contrast(1.5)",
  "&:hover, &:focus-visible": {
    filter: "none",
  },
});

const SLink = styled(Link)({
  textDecoration: "none",
  "&:hover": {
    textDecoration: "underline",
  },
});

export {
  SiteHeader,
  searchInputHeight,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
};
